import Ajv, { type ErrorObject, type ValidateFunction } from 'ajv'
import {
  destinationComponentDefinition,
  DESTINATION_MANIFEST,
} from './componentManifest'
import { validateLayout, type ValidationError, type ValidationResult } from '../layout/schema'
import type { Layout } from '../layout'

const ajv = new Ajv({ allErrors: true, strict: true })
const configValidators = new Map<string, ValidateFunction>()
const liveStateValidators = new Map<string, ValidateFunction>()

function schemaErrors(path: string, errors: ErrorObject[] | null | undefined): ValidationError[] {
  return (errors ?? []).map((error) => ({
    path: `${path}${error.instancePath.replace(/\//g, '.')}`,
    message: error.message ?? 'does not match the component schema',
  }))
}

function componentValidator(
  componentName: string,
  kind: 'config' | 'liveState',
): ValidateFunction | null {
  const definition = destinationComponentDefinition(componentName)
  if (!definition) return null
  const schema = kind === 'config' ? definition.configSchema : definition.liveStateSchema
  if (!schema) return null
  const cache = kind === 'config' ? configValidators : liveStateValidators
  let validator = cache.get(definition.name)
  if (!validator) {
    validator = ajv.compile(schema)
    cache.set(definition.name, validator)
  }
  return validator
}

/**
 * Validates layout structure, canonical component names, component config,
 * and shell placement capabilities. The returned layout is safe to persist
 * and contains canonical names only.
 */
export function validateDestinationLayout(input: unknown): ValidationResult<Layout> {
  const structural = validateLayout(input)
  if (!structural.ok) return structural

  const layout = structural.value
  const shell = DESTINATION_MANIFEST.shells.find((candidate) => candidate.name === layout.shell)
  if (!shell) {
    return { ok: false, errors: [{ path: '$.shell', message: 'shell is not registered' }] }
  }

  const errors: ValidationError[] = []
  const instanceIds = new Set<string>()
  const slots: Layout['slots'] = {}

  for (const [slotName, instances] of Object.entries(layout.slots)) {
    const placements = shell.slots[slotName]
    if (!placements) {
      errors.push({
        path: `$.slots.${slotName}`,
        message: `slot is not declared by shell ${layout.shell}`,
      })
      continue
    }

    slots[slotName] = instances.map((instance, index) => {
      const path = `$.slots.${slotName}[${index}]`
      if (instanceIds.has(instance.id)) {
        errors.push({ path: `${path}.id`, message: 'id must be unique across the layout' })
      }
      instanceIds.add(instance.id)

      const definition = destinationComponentDefinition(instance.primitive)
      if (!definition) {
        errors.push({ path: `${path}.primitive`, message: 'component is not registered' })
        return instance
      }
      if (!definition.placements.some((placement) => placements.includes(placement))) {
        errors.push({
          path: `${path}.primitive`,
          message: `${definition.name} is not valid in this slot`,
        })
      }

      const validator = componentValidator(definition.name, 'config')
      if (validator && !validator(instance.config)) {
        errors.push(...schemaErrors(`${path}.config`, validator.errors))
      }

      return { ...instance, primitive: definition.name }
    })
  }

  for (const slotName of Object.keys(shell.slots)) slots[slotName] ??= []

  return errors.length > 0
    ? { ok: false, errors }
    : { ok: true, value: { ...layout, slots } }
}

export function validateDestinationLiveState(
  componentName: string,
  input: unknown,
): ValidationResult<Record<string, unknown>> {
  const definition = destinationComponentDefinition(componentName)
  if (!definition) {
    return {
      ok: false,
      errors: [{ path: '$', message: 'component is not registered' }],
    }
  }
  const validator = componentValidator(definition.name, 'liveState')
  if (!validator) {
    return {
      ok: false,
      errors: [{ path: '$', message: 'component does not accept live state' }],
    }
  }
  if (!validator(input)) {
    return { ok: false, errors: schemaErrors('$', validator.errors) }
  }
  return { ok: true, value: input as Record<string, unknown> }
}
